import LazyWidgetController from "./lazy_widget_controller"

// Stimulus controller wrapping Choices.js for enhanced <select> elements.
//
// Choices re-parents the <select> into a `.choices` wrapper on init, so the
// re-parent guard + Turbo-cache-safe teardown in LazyWidgetController are what
// keep it from looping (the 16 GB memory runaway) and double-initialising on a
// cached restore. This subclass only supplies load()/build().
//
// Reads the SAME classes/data-* attributes the legacy `$('[data-choices]').each`
// did, so behaviour is identical:
//   - .choices-search-enabled            -> searchEnabled
//   - [data-choices-groups]              -> placeholderValue
//   - [data-choices-removeItem] / [data-choices-multiple-remove] -> removeItemButton
//   - [data-choices-limit]               -> maxItemCount
//   - [data-choices-text-unique-true]    -> duplicateItemsAllowed (negated)
//   - [data-choices-text-disabled-true]  -> addItems (negated) + .disable()
//   - .member-select                     -> avatar item/choice templates
//   - .calendar-event-statuses-select / -kinds-select / -visibilities-select
//                                        -> icon + description templates
//
// Usage in views (rendered by AppFormBuilder app_select / member_select):
//   <select data-controller="choices" data-choices class="member-select" ...>
//
export default class extends LazyWidgetController {
  async load() {
    return (await import("choices.js")).default
  }

  build(Choices) {
    const el = this.element

    const options = {
      placeholderValue: el.hasAttribute('data-choices-groups')
        ? 'This is a placeholder set in the config'
        : undefined,
      searchEnabled: el.classList.contains('choices-search-enabled'),
      removeItemButton:
        el.hasAttribute('data-choices-removeItem') ||
        el.hasAttribute('data-choices-multiple-remove'),
      shouldSort: false,
      maxItemCount: el.getAttribute('data-choices-limit') || undefined,
      duplicateItemsAllowed: !el.hasAttribute('data-choices-text-unique-true'),
      addItems: !el.hasAttribute('data-choices-text-disabled-true')
    }

    // Tighten the fuzzy search (Choices drives it through Fuse.js): a lower threshold
    // means stricter matching (0 = exact). Opt-in via data-choices-fuse-threshold.
    const fuse = el.getAttribute('data-choices-fuse-threshold')
    if (fuse !== null) options.fuseOptions = { threshold: parseFloat(fuse) }
    // Choices caps search results at 4 by default — raise it so filtering a long
    // option list still surfaces every match. Opt-in via data-choices-search-limit.
    const searchLimit = el.getAttribute('data-choices-search-limit')
    if (searchLimit !== null) options.searchResultLimit = parseInt(searchLimit, 10)

    if (el.classList.contains('member-select')) {
      options.classNames = {
        containerOuter: ['choices', 'member-select'],
      }
      options.callbackOnCreateTemplates = function (strToEl, escapeForTemplate, getClassNames) {
        return {
          item: ({ classNames }, data) => {
            var label = data.element.dataset.label || data.label
            var avatar = ''
            if (data.value.length > 0) {
              if (data.element.dataset.avatar === undefined) {
                avatar = `<span class="avatar-placeholder"><i class="fa-duotone fa-light fa-2xl fa-circle-user"></i></span>`
              } else {
                avatar = `<span class="avatar-wrapper"><img src="${data.element.dataset.avatar}" alt="${escapeForTemplate(data.label)}" class="img-fluid rounded"></span>`
              }
            }
            var company = ''
            if (data.element.dataset.company !== undefined) {
              company = ` <div class="company-name">${escapeForTemplate(true, data.element.dataset.company)}</div>`
            }

            return strToEl(`
              <div class="item-with-avatar ${getClassNames(classNames.item).join(' ')} ${
              getClassNames(data.highlighted
                ? classNames.highlightedState
                : classNames.itemSelectable).join(' ')
            } ${
              data.placeholder ? classNames.placeholder : ''
            }" data-item data-id="${data.id}" data-value="${escapeForTemplate(data.value)}" ${
              data.active ? 'aria-selected="true"' : ''
            } ${data.disabled ? 'aria-disabled="true"' : ''}>
                ${avatar} <div class="item-label-wrapper"><div class="item-label">${escapeForTemplate(true, label)}</div> ${company}</div>
              </div>
            `)
          },
          choice: ({ classNames }, data) => {
            var label = data.element.dataset.label || data.label
            var avatar = ''
            if (data.value.length > 0) {
              if (data.element.dataset.avatar === undefined) {
                avatar = `<span class="avatar-placeholder"><i class="fa-duotone fa-light fa-2xl fa-circle-user"></i></span>`
              } else {
                avatar = `<span class="avatar-wrapper"><img src="${data.element.dataset.avatar}" alt="${escapeForTemplate(data.label)}" class="img-fluid rounded"></span>`
              }
            }
            var company = ''
            if (data.element.dataset.company !== undefined) {
              company = ` <div class="company-name">${escapeForTemplate(true, data.element.dataset.company)}</div>`
            }

            return strToEl(`
              <div class="item-with-avatar ${getClassNames(classNames.item).join(' ')} ${getClassNames(classNames.itemChoice).join(' ')} ${
              getClassNames(data.disabled ? classNames.itemDisabled : classNames.itemSelectable).join(' ')
            }" data-select-text="${this.config.itemSelectText}" data-choice ${
              data.disabled
                ? 'data-choice-disabled aria-disabled="true"'
                : 'data-choice-selectable'
            } data-id="${data.id}" data-value="${escapeForTemplate(data.value)}" ${
              data.groupId > 0 ? 'role="treeitem"' : 'role="option"'
            }>
                ${avatar} <div class="item-label-wrapper"><div class="item-label">${escapeForTemplate(true, label)}</div> ${company}</div>
              </div>
            `)
          },
        }
      }
    }

    if (
      el.classList.contains('calendar-event-statuses-select') ||
      el.classList.contains('calendar-event-kinds-select') ||
      el.classList.contains('calendar-event-visibilities-select')
    ) {
      var containerClass = el.classList.contains('calendar-event-statuses-select') ? 'calendar-event-statuses-select'
                         : el.classList.contains('calendar-event-kinds-select') ? 'calendar-event-kinds-select'
                         : 'calendar-event-visibilities-select'
      options.classNames = {
        containerOuter: ['choices', containerClass],
      }
      options.callbackOnCreateTemplates = function (strToEl, escapeForTemplate, getClassNames) {
        var renderItem = function (classNames, data, isChoice) {
          var label = data.element.dataset.label || data.label
          var icon = ''
          if (data.value.length > 0) {
            icon = `<span class="icon-wrapper"><i class="fa-duotone fa-light fa-2xl fa-${data.element.dataset.icon}" style="color: ${data.element.dataset.color};"></i></span>`
          }
          var description = ''
          if (data.element.dataset.description !== undefined) {
            description = ` <div class="option-description">${escapeForTemplate(true, data.element.dataset.description)}</div>`
          }

          if (isChoice) {
            return strToEl(`
              <div class="item-with-icon-and-description ${getClassNames(classNames.item).join(' ')} ${getClassNames(classNames.itemChoice).join(' ')} ${
              getClassNames(data.disabled ? classNames.itemDisabled : classNames.itemSelectable).join(' ')
            }" data-select-text="${this.config.itemSelectText}" data-choice ${
              data.disabled
                ? 'data-choice-disabled aria-disabled="true"'
                : 'data-choice-selectable'
            } data-id="${data.id}" data-value="${escapeForTemplate(data.value)}" ${
              data.groupId > 0 ? 'role="treeitem"' : 'role="option"'
            }>
                ${icon} <div class="item-label-wrapper"><div class="item-label">${escapeForTemplate(true, label)}</div> ${description}</div>
              </div>
            `)
          }
          return strToEl(`
            <div class="item-with-icon-and-description ${getClassNames(classNames.item).join(' ')} ${
            getClassNames(data.highlighted
              ? classNames.highlightedState
              : classNames.itemSelectable).join(' ')
          } ${
            data.placeholder ? classNames.placeholder : ''
          }" data-item data-id="${data.id}" data-value="${escapeForTemplate(data.value)}" ${
            data.active ? 'aria-selected="true"' : ''
          } ${data.disabled ? 'aria-disabled="true"' : ''}>
              ${icon} <div class="item-label-wrapper"><div class="item-label">${escapeForTemplate(true, label)}</div> ${description}</div>
            </div>
          `)
        }

        return {
          item: function ({ classNames }, data) { return renderItem.call(this, classNames, data, false) },
          choice: function ({ classNames }, data) { return renderItem.call(this, classNames, data, true) },
        }
      }
    }

    const choices = new Choices(el, options)

    if (el.hasAttribute('data-choices-text-disabled-true')) {
      choices.disable()
    }

    return choices
  }

  // destroy() inherited from LazyWidgetController: Choices instance has .destroy().
}
